/**
 * ValidationPanel component.
 * Displays validation results in a compact format suitable for embedding in dialogs.
 * Shows errors as red items, warnings as yellow, and a success state when all checks pass.
 */

import type { ReactElement } from "react";

import type { ConfigValidationResult, ConfigValidationIssue } from "../diff/types";

// ============================================================================
// Types
// ============================================================================

/** Props for ValidationPanel component */
export interface ValidationPanelProps {
  /** Validation result from API */
  readonly validation: ConfigValidationResult | null;
  /** Whether validation is currently running */
  readonly isValidating: boolean;
  /** Error message from validation attempt */
  readonly error?: string | null | undefined;
  /** Maximum number of issues to display before "and N more" */
  readonly maxIssues?: number | undefined;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ValidationPanel displays config validation results in a compact format.
 */
export function ValidationPanel({
  validation,
  isValidating,
  error,
  maxIssues = 10,
}: ValidationPanelProps): ReactElement {
  if (isValidating) {
    return (
      <div
        className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
        data-testid="validation-panel"
      >
        <div className="flex items-center gap-2">
          <SpinnerIcon className="w-5 h-5 text-blue-500 animate-spin" />
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            Running validation...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
        data-testid="validation-panel"
      >
        <div className="flex items-center gap-2 mb-1">
          <ErrorIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-sm font-medium text-red-700 dark:text-red-300">
            Validation failed
          </span>
        </div>
        <p className="text-xs text-red-600 dark:text-red-400 ml-7">{error}</p>
      </div>
    );
  }

  if (!validation) {
    return (
      <div
        className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-center"
        data-testid="validation-panel"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">Validation has not been run yet</p>
      </div>
    );
  }

  const allIssues: readonly ConfigValidationIssue[] = [
    ...validation.schemaErrors,
    ...validation.referenceErrors,
  ];
  const errorCount = allIssues.filter((i) => i.severity === "error").length;
  const warningCount = allIssues.filter((i) => i.severity === "warning").length;

  const borderColor = validation.canPromote
    ? "border-green-200 dark:border-green-800"
    : "border-red-200 dark:border-red-800";
  const bgColor = validation.canPromote
    ? "bg-green-50 dark:bg-green-900/20"
    : "bg-red-50 dark:bg-red-900/20";

  return (
    <div
      className={`p-4 rounded-lg border ${bgColor} ${borderColor}`}
      data-testid="validation-panel"
    >
      {/* Status header */}
      <div className="flex items-center gap-2 mb-2">
        {validation.canPromote ? (
          <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
        ) : (
          <ErrorIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
        )}
        <span
          className={`font-medium text-sm ${
            validation.canPromote
              ? "text-green-800 dark:text-green-300"
              : "text-red-800 dark:text-red-300"
          }`}
        >
          {validation.canPromote ? "All checks passed" : "Validation failed"}
        </span>
      </div>

      {/* Check summary row */}
      <div className="flex gap-4 text-xs mb-2 ml-7">
        <StatusCheck label="Schema" passed={validation.schemaValid} />
        <StatusCheck label="References" passed={validation.referencesValid} />
        {errorCount > 0 && (
          <span className="text-red-600 dark:text-red-400">
            {errorCount} error{errorCount !== 1 ? "s" : ""}
          </span>
        )}
        {warningCount > 0 && (
          <span className="text-amber-600 dark:text-amber-400">
            {warningCount} warning{warningCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Issue list */}
      {allIssues.length > 0 && (
        <div className="mt-3 ml-7 space-y-1">
          {allIssues.slice(0, maxIssues).map((issue, idx) => (
            <IssueItem key={`${issue.path}-${issue.code}-${idx}`} issue={issue} />
          ))}
          {allIssues.length > maxIssues && (
            <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
              ... and {allIssues.length - maxIssues} more issues
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface StatusCheckProps {
  readonly label: string;
  readonly passed: boolean;
}

function StatusCheck({ label, passed }: StatusCheckProps): ReactElement {
  return (
    <div className="flex items-center gap-1">
      {passed ? (
        <CheckIcon className="w-3.5 h-3.5 text-green-500" />
      ) : (
        <ErrorIcon className="w-3.5 h-3.5 text-red-500" />
      )}
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
    </div>
  );
}

interface IssueItemProps {
  readonly issue: ConfigValidationIssue;
}

function IssueItem({ issue }: IssueItemProps): ReactElement {
  const colors: Record<string, string> = {
    error: "text-red-600 dark:text-red-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",
  };

  const bulletColors: Record<string, string> = {
    error: "bg-red-500",
    warning: "bg-amber-500",
    info: "bg-blue-500",
  };

  return (
    <div className="flex items-start gap-2 text-xs">
      <span
        className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${bulletColors[issue.severity] ?? "bg-gray-500"}`}
      />
      <div>
        <span className={colors[issue.severity] ?? "text-gray-600"}>[{issue.code}]</span>{" "}
        <span className="text-gray-500 dark:text-gray-400">{issue.path}:</span>{" "}
        <span className="text-gray-800 dark:text-gray-200">{issue.message}</span>
      </div>
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function SpinnerIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ErrorIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

export default ValidationPanel;
